import React, { useState, useEffect } from "react";
import { valuationFooter } from "../utils/valuationDisclosure";
import jsPDF from "jspdf";
import { supabase } from "../lib/supabaseClient";
import { getRetainedValuations } from "../lib/getRetainedValuations";
import { loadLogoForPdf } from "../lib/pdfLogo";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

// helper dimensions dans le PDF
const getDimensionsForPDF = (aw) => {
  // 1. Champ `dimensions` direct dans la table
  // ex: "120 x 80 cm", "243.9 x 233.7 cm", "180 cm hauteur"
  if (aw.dimensions && typeof aw.dimensions === "string" && aw.dimensions.trim() !== "") {
    return aw.dimensions.trim();
  }

  // 2. specs JSON dans `specifications`
  // ex: specifications = '{"dimensions":{"unit":"cm","width":"70","height":"90","depth":""}}'
  if (aw.specifications) {
    let specsObj = aw.specifications;
    if (typeof specsObj === "string") {
      try {
        specsObj = JSON.parse(specsObj);
      } catch {
        specsObj = null;
      }
    }
    if (specsObj && typeof specsObj === "object" && specsObj.dimensions) {
      const d = specsObj.dimensions;
      const unit = d.unit || "cm";

      const h = d.height || d.H || d.h;
      const w = d.width  || d.W || d.w;
      const dep = d.depth || d.D || d.d;

      // cas largeur x hauteur x profondeur
      if (w && h && dep && dep !== "0" && dep !== "") {
        return `${h} x ${w} x ${dep} ${unit}`;
      }
      // cas largeur x hauteur
      if (w && h) {
        return `${h} x ${w} ${unit}`;
      }
      // cas hauteur seule (ex: Rodin 180 cm hauteur)
      if (h) {
        return `${h} ${unit}`;
      }
    }
  }

  // 3. colonnes height / width / depth / unit directes en base
  // ex: height='50', width='70', depth='5', unit='cm'
  const hCol = aw.height;
  const wCol = aw.width;
  const dCol = aw.depth;
  const unitCol = aw.unit || "cm";

  if (hCol || wCol || dCol) {
    // on reconstruit proprement
    if (hCol && wCol && dCol && dCol !== "0" && dCol !== "") {
      return `${hCol} x ${wCol} x ${dCol} ${unitCol}`;
    }
    if (hCol && wCol) {
      return `${hCol} x ${wCol} ${unitCol}`;
    }
    if (hCol && !wCol && !dCol) {
      // ex: Rodin "180 cm hauteur"
      return `${hCol} ${unitCol}`;
    }
  }

  // 4. fallback très basique :
  //   parfois les dimensions sont mentionnées dans la description ("115 x 109 x 2 cm")
  if (aw.description && typeof aw.description === "string") {
    // petit regex basique genre "nombre x nombre x (nombre)? cm"
    const match = aw.description.match(/(\d+(\.\d+)?)\s*x\s*(\d+(\.\d+)?)(\s*x\s*(\d+(\.\d+)?))?\s*cm/i);
    if (match) {
      return match[0];
    }
  }

  return "-";
};

// helper image pour les miniatures PDF
// On n'utilise que image_url (string simple) — le champ images JSONB
// n'est plus chargé pour éviter le RangeError dans jsPDF
const getImageForPDF = (a) => {
  if (a.image_url && !a.image_url.startsWith("data:")) return a.image_url;
  return null;
};

// formatage EUR compatible jsPDF
const formatMoneyEUR = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return "—";
  const fr = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
  }).format(num);
  const clean = fr.replace(/\u202F/g, " "); // enlève espace fine
  return `${clean} EUR`;
};



const parseMaybeJSON = (value, fallback) => {
  if (!value) return fallback;
  try {
    if (typeof value === "string") return JSON.parse(value);
    if (typeof value === "object") return value;
    return fallback;
  } catch {
    return fallback;
  }
};

const inferDocumentType = (doc) => {
  const raw = typeof doc === "string"
    ? doc
    : [
        doc?.type,
        doc?.document_type,
        doc?.category,
        doc?.name,
        doc?.title,
        doc?.filename,
        doc?.file,
        doc?.url,
        doc?.path,
      ].filter(Boolean).join(" ");

  const text = String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (/invoice|facture|receipt|recu|achat|purchase/.test(text)) return "invoice";
  if (/certificate|certificat|authenticite|authenticity|coa/.test(text)) return "certificate";
  if (/condition|conservation|constat|etat/.test(text)) return "condition_report";
  if (/assurance|insurance/.test(text)) return "insurance";
  return "document";
};

const normalizeDocs = (docs) => {
  if (!docs) return [];

  // Les anciens imports peuvent stocker soit un JSON, soit une URL/chemin brut en texte.
  // Avant, une string non JSON retombait à [] : les factures de lot disparaissaient du rapport.
  if (typeof docs === "string") {
    const trimmed = docs.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
      return arr.filter(Boolean);
    } catch {
      return [trimmed];
    }
  }

  const arr = Array.isArray(docs) ? docs : [docs];
  return arr.filter(Boolean);
};

const buildLotInheritedDocuments = (lot) => {
  if (!lot) return [];
  const lotTitle = lot.title || "Lot";
  const docs = normalizeDocs(lot.documents).map((doc, index) => {
    const inferredType = inferDocumentType(doc);
    if (typeof doc === "string") {
      return { id: `lot-doc-${index}-${doc}`, name: `Document du lot — ${lotTitle}`, file: doc, url: doc, type: inferredType, document_type: inferredType, source: "lot" };
    }
    return {
      ...doc,
      id: doc.id || doc.path || doc.url || doc.file || `lot-doc-${index}`,
      name: doc.name || doc.title || doc.filename || `Document du lot — ${lotTitle}`,
      type: doc.type || doc.document_type || inferredType,
      document_type: doc.document_type || doc.type || inferredType,
      source: "lot",
    };
  });

  if (lot.invoice === true && !docs.some(d => d.type === "invoice" || d.document_type === "invoice")) {
    docs.push({ id: `lot-invoice-${lot.id || lotTitle}`, name: `Facture générale du lot — ${lotTitle}`, type: "invoice", source: "lot", noFile: true });
  }
  if ((lot.certificate_auth === true || lot.certificate_authenticity) && !docs.some(d => d.type === "certificate" || d.document_type === "certificate")) {
    docs.push({ id: `lot-certificate-${lot.id || lotTitle}`, name: `Certificat général du lot — ${lotTitle}`, type: "certificate", source: "lot", noFile: !lot.certificate_authenticity || lot.certificate_authenticity === true });
  }
  return docs;
};

const effectiveDocuments = (aw) => {
  const ownDocs = normalizeDocs(aw?.documents);
  const lotDocs = buildLotInheritedDocuments(aw?.lot);
  const seen = new Set();
  return [...ownDocs, ...lotDocs].filter((doc) => {
    const key = typeof doc === "string" ? doc : (doc.id || doc.file || doc.url || doc.path || `${doc.name}-${doc.type}-${doc.source}`);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const hasDocumentForCompleteness = (aw) => {
  return Boolean(
    effectiveDocuments(aw).length > 0 ||
    aw?.invoice === true ||
    aw?.certificate_auth === true ||
    aw?.certificate_blockchain === true ||
    aw?.certificate_authenticity ||
    aw?.condition_report ||
    aw?.condition_report_url ||
    aw?.lot?.invoice === true ||
    aw?.lot?.certificate_auth === true ||
    aw?.lot?.certificate_authenticity
  );
};

const hasImageForCompleteness = (aw) => {
  const imgs = parseMaybeJSON(aw?.images, []);
  return Boolean(
    aw?.image_url ||
    aw?.primary_image_path ||
    (Array.isArray(imgs) && imgs.length > 0) ||
    aw?.lot?.reference_image_url
  );
};

const hasMediumForCompleteness = (aw) => Boolean(
  (aw?.medium && String(aw.medium).trim()) ||
  (aw?.medium_original && String(aw.medium_original).trim()) ||
  (aw?.medium_normalized_key && String(aw.medium_normalized_key).trim()) ||
  (aw?.medium_category && String(aw.medium_category).trim()) ||
  (aw?.lot?.technique && String(aw.lot.technique).trim())
);

const hasArtistForCompleteness = (aw) => Boolean(
  (aw?.artist && String(aw.artist).trim()) ||
  (aw?.lot?.artist && String(aw.lot.artist).trim())
);

const inventorySortKey = (aw) => String(aw?.inventory_number || aw?.lot_copy_number || aw?.title || "").toLocaleLowerCase("fr-FR");
const CollectionsReports = () => {
  const { t } = useTranslation();
  const tr = (key, options) => t(`collectionReports.${key}`, options);
  const { id } = useParams(); // collection_id éventuel
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [artworks, setArtworks] = useState([]);
  const [, setCollection] = useState(null);

  const [reportName, setReportName] = useState(() => tr("types.inventory"));
  const [reportType, setReportType] = useState("inventory");
  const [clientId, setClientId] = useState(null);

  // Rôle et sélection collection/collectionneur
  const [isAdvisor, setIsAdvisor] = useState(false);
  const [collectors, setCollectors] = useState([]);
  const [selectedCollectorId, setSelectedCollectorId] = useState("");
  const [ownCollections, setOwnCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");

  // États pour les rapports blancs
  const [userProfile, setUserProfile] = useState(null);
  const [enableWhiteLabel, setEnableWhiteLabel] = useState(false);

  const [errorMsg, setErrorMsg] = useState(null);

  const reportTypes = ["inventory", "artist", "location", "valuation", "insurance", "exhibition", "acquisition", "condition", "completude"]
    .map((typeId) => ({ id: typeId, name: tr(`types.${typeId}`) }));

  const [options, setOptions] = useState({
    includeDate: true,
    includeThumbnails: true,
    includeValueTotal: false,
    includePurchaseValue: false,
    includeTitle: true,
    includeArtist: true,
    includeLocation: true,
    includePrice: true,
    includeValueSource: true,
    includeSize: true,
    includeMedium: true,
    groupBy: "none",
    sortBy: "name",
    // Complétude
    checkImage: true,
    checkArtist: true,
    checkDimensions: true,
    checkMedium: true,
    checkYear: true,
    checkEstimatedValue: true,
    checkDocuments: true,
    checkInventoryNumber: false,
    checkDescription: false,
    checkAcquisitionPrice: false,
  });

  useEffect(() => {
    if (!user) return;
    setClientId(user.id);
    fetchUserProfile(user.id);
    initRoleAndCollections(user.id);
    if (id && id !== "reports") {
      fetchCollectionInfo(id);
      setSelectedCollectionId(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  // Initialise le rôle, charge les collectionneurs (si expert) ou les collections (si collectionneur)
  const initRoleAndCollections = async (userId) => {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("role, plan")
        .eq("id", userId)
        .maybeSingle();

      const advisor = profile?.role === "expert";
      setIsAdvisor(advisor);

      if (advisor) {
        // Récupérer les collectionneurs dont l'advisor a des permissions
        const { data: perms } = await supabase
          .from("collection_permissions")
          .select("collection_id, client_id")
          .eq("grantee_id", userId);

        const clientIds = [...new Set((perms || []).map(p => p.client_id).filter(Boolean))];

        if (clientIds.length > 0) {
          // Charger les profils des collectionneurs (email depuis auth.users n'est pas accessible directement)
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, company_name")
            .in("id", clientIds);
          setCollectors(profiles || []);
        }

        // Charger toutes les collections partagées
        const sharedColIds = [...new Set((perms || []).map(p => p.collection_id).filter(Boolean))];
        if (sharedColIds.length > 0) {
          const { data: cols } = await supabase
            .from("collections")
            .select("id, title, client_id")
            .in("id", sharedColIds);
          setOwnCollections(cols || []);
        }
      } else {
        // Collectionneur : ses propres collections
        const { data: cols } = await supabase
          .from("collections")
          .select("id, title")
          .eq("client_id", userId)
          .order("title");
        setOwnCollections(cols || []);
      }

      // Charger les œuvres après avoir le contexte
      fetchArtworks(userId);
    } catch (err) {
      console.error("Erreur init rôle:", err);
      fetchArtworks(userId);
    }
  };

  // Recharger les œuvres quand la collection ou le collectionneur change
  useEffect(() => {
    if (!user?.id) return;
    fetchArtworks(user.id);
    // Mettre à jour les collections disponibles selon le collectionneur sélectionné
    if (isAdvisor && selectedCollectorId) {
      const filtered = ownCollections.filter(c => c.client_id === selectedCollectorId);
      // Si la collection sélectionnée n'appartient plus au collectionneur, reset
      if (selectedCollectionId && !filtered.find(c => c.id === selectedCollectionId)) {
        setSelectedCollectionId("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCollectorId, selectedCollectionId]);

  const fetchUserProfile = async (userId) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("company_name, company, first_name, last_name, plan, advisor_logo_url, advisor_logo_public_url, advisor_title")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) return;

      const fullName = [profile?.first_name, profile?.last_name]
        .filter(Boolean)
        .join(" ");

      let companyLogoUrl = profile.advisor_logo_public_url || profile.advisor_logo_url || null;
      let companyDisplayName = profile.company_name || profile.company || fullName || "Expert";

      // Si l'utilisateur est un client géré par un Family Office,
      // on récupère le logo et le nom du FO pour les rapports blancs
      const { data: foLink } = await supabase
        .from("family_office_clients")
        .select("family_office_id")
        .eq("family_client_id", userId)
        .maybeSingle();

      if (foLink?.family_office_id) {
        const { data: foProfile } = await supabase
          .from("profiles")
          .select("company_name, company, advisor_logo_url, advisor_logo_public_url, first_name, last_name")
          .eq("id", foLink.family_office_id)
          .maybeSingle();

        if (foProfile) {
          companyLogoUrl = foProfile.advisor_logo_public_url || foProfile.advisor_logo_url || companyLogoUrl;
          companyDisplayName =
            foProfile.company_name ||
            foProfile.company ||
            [foProfile.first_name, foProfile.last_name].filter(Boolean).join(" ") ||
            companyDisplayName;
        }
      }

      setUserProfile({
        ...profile,
        full_name: fullName || "Expert",
        company_logo_url: companyLogoUrl,
        company_display_name: companyDisplayName,
      });

      const whiteLabelPlans = ["art_advisor_premium", "family_office"];
      if (whiteLabelPlans.includes(profile?.plan)) {
        setEnableWhiteLabel(true);
      }
    } catch (error) {
      console.error("Erreur chargement profil:", error);
    }
  };

  const fetchArtworks = async (userId) => {
    try {
      setIsLoading(true);

      const AW_SELECT = "id, title, artist, year, medium, medium_original, medium_normalized_key, medium_category, dimensions, value, image_url, primary_image_path, images, documents, invoice, certificate_auth, certificate_blockchain, certificate_authenticity, condition_report, condition_report_url, collection_id, location_id, client_id, created_at, description, price, provenance, acquisitionDetails, specifications, tags, height, width, depth, unit, condition, conditionNotes, isEdition, editionInfo, availability, typeOfArt, estimated_value, creationDate, styles, acquisition_date, acquisition_price, acquisition_type, acquisition_source, price_low, price_high, inventory_number, current_estimated_value, valuation_market_value, valuation_consensus, lot_id, lot_copy_number, insurance_status, loan_status, artwork_status, documentation_status, storage_risk";

      // Détecter si advisor
      const { data: profile } = await supabase
        .from("profiles")
        .select("role, plan")
        .eq("id", userId)
        .maybeSingle();

      const advisorRole = profile?.role === "expert";
      let artworksData = [];

      // Utiliser la collection sélectionnée dans le formulaire (priorité) ou l'URL
      const activeCollectionId = selectedCollectionId || (id && id !== "reports" ? id : null);

      if (advisorRole) {
        // Œuvres des collections partagées
        const { data: perms } = await supabase
          .from("collection_permissions")
          .select("collection_id, client_id")
          .eq("grantee_id", userId);

        let sharedCollectionIds = (perms || []).map(p => p.collection_id).filter(Boolean);

        // Filtrer par collectionneur sélectionné
        if (selectedCollectorId) {
          const collectorPerms = (perms || []).filter(p => p.client_id === selectedCollectorId);
          sharedCollectionIds = collectorPerms.map(p => p.collection_id).filter(Boolean);
        }

        if (sharedCollectionIds.length > 0) {
          let q = supabase.from("artworks").select(AW_SELECT).in("collection_id", sharedCollectionIds);
          if (activeCollectionId) q = q.eq("collection_id", activeCollectionId);
          const { data, error } = await q;
          if (error) throw error;
          artworksData = data || [];
        }
      } else {
        // Collecteur : ses propres œuvres
        let q = supabase.from("artworks").select(AW_SELECT).eq("client_id", userId);
        if (activeCollectionId) q = q.eq("collection_id", activeCollectionId);
        else q = applyProposalFilter(q, await proposalCollectionIds(userId));
        const { data, error } = await q;
        if (error) throw error;
        artworksData = data || [];
      }

      // Résoudre les métadonnées de lots pour que les factures/certificats de lot
      // couvrent correctement les œuvres enfants dans les rapports de complétude.
      const lotIds = [...new Set((artworksData || []).map(a => a.lot_id).filter(Boolean))];
      if (lotIds.length > 0) {
        let lotRows = [];
        let { data: lotsFull, error: lotsFullErr } = await supabase
          .from("lots")
          .select("id, title, artist, technique, total_copies, edition_info, reference_image_url, documents, invoice, certificate_auth, certificate_authenticity")
          .in("id", lotIds);

        if (!lotsFullErr) {
          lotRows = lotsFull || [];
        } else {
          const { data: lotsBasic, error: lotsBasicErr } = await supabase
            .from("lots")
            .select("id, title, artist, technique, total_copies, edition_info, reference_image_url")
            .in("id", lotIds);
          if (!lotsBasicErr) lotRows = lotsBasic || [];
        }

        const lotMap = Object.fromEntries((lotRows || []).map(l => [l.id, l]));
        artworksData = (artworksData || []).map(a => ({ ...a, lot: a.lot_id ? lotMap[a.lot_id] || null : null }));
      }

      // Résoudre les noms d'emplacement depuis la table locations
      const locationIds = [...new Set(artworksData.map(a => a.location_id).filter(Boolean))];
      console.log("[DEBUG] location_ids trouvés:", locationIds.length, locationIds.slice(0,3));
      let locationMap = {};
      if (locationIds.length > 0) {
        const { data: locs, error: locError } = await supabase
          .from("locations")
          .select("id, name, temperature, humidity, light, ventilation, security_measures, storage_risk, storage_notes, address")
          .in("id", locationIds);
        console.log("[DEBUG] locations retournées:", locs?.length, "erreur:", locError?.message);
        if (locs) {
          locationMap = locs.reduce((acc, l) => { acc[l.id] = l; return acc; }, {});
        }
      }

      // Injecter location_name sur chaque œuvre
      const enriched = artworksData.map(a => ({
        ...a,
        location_name: a.location_id ? (locationMap[a.location_id]?.name || "-") : "-",
        location_data: a.location_id ? (locationMap[a.location_id] || null) : null,
      }));

      setArtworks(enriched);
    } catch (error) {
      console.error("Erreur chargement œuvres:", error);
      setErrorMsg(
        `Erreur lors du chargement des œuvres: ${
          error.message || error.toString()
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCollectionInfo = async (collectionId) => {
    if (!collectionId) return;

    try {
      const { data, error } = await supabase
        .from("collections")
        .select("*")
        .eq("id", collectionId)
        .single();

      if (error) throw error;

      setCollection(data);

      if (data) {
        setReportName(`Inventaire - ${data.title || ""}`);
      }
    } catch (error) {
      console.error("Erreur chargement collection:", error);
      setErrorMsg(
        `Erreur lors du chargement de la collection: ${
          error.message || error.toString()
        }`
      );
    }
  };

  const handleReportTypeChange = (type) => {
    setReportType(type);

    const selectedType = reportTypes.find((r) => r.id === type);
    if (selectedType) {
      setReportName(selectedType.name);
    }

    let newOptions = {
      includeDate: true,
      includeThumbnails: false,  // désactivé par défaut — images HD trop lourdes
      includeTitle: true,
      includeValueTotal: false,
      includePurchaseValue: false,
      includeArtist: true,
      includeLocation: true,
      includePrice: true,
      includeSize: true,
      includeMedium: true,
      groupBy: "none",
      sortBy: "name",
    };

    switch (type) {
      case "artist":
        newOptions.groupBy = "artist";
        break;
      case "valuation":
        newOptions.includeValueTotal = true;
        newOptions.includePurchaseValue = true;
        newOptions.includeThumbnails = false;
        newOptions.groupBy = "artist";
        break;
      case "location":
        newOptions.groupBy = "location";
        break;
      case "insurance":
        newOptions.includeValueTotal = true;
        // pour assurance on veut souvent montrer prix estimé + assuré
        break;
      case "exhibition":
        newOptions.includeThumbnails = true; // seul rapport où les vignettes ont du sens
        break;
      case "acquisition":
        newOptions.includePurchaseValue = true;
        newOptions.sortBy = "acquisitionDate";
        break;
      case "completude":
        newOptions.includeThumbnails = false;
        newOptions.groupBy = "artist";
        newOptions.checkImage = true;
        newOptions.checkArtist = true;
        newOptions.checkDimensions = true;
        newOptions.checkMedium = true;
        newOptions.checkYear = true;
        newOptions.checkEstimatedValue = true;
        newOptions.checkDocuments = true;
        break;
      default:
        break;
    }

    setOptions(newOptions);
  };

  // ======== PDF GENERATION ======== //
  const generatePDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");

    // Traçabilité des valeurs (§P3→P4) : valeur retenue + source à citer dans le rapport.
    let retainedMap = {};
    if (options.includePrice) {
      try { retainedMap = await getRetainedValuations(artworks.map((a) => a.id).filter(Boolean)); }
      catch { retainedMap = {}; }
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // charge les images (si demandé) — limité à 50 miniatures max
    // et traité par batch de 10 pour éviter les timeouts et le RangeError
    const imagesMap = {};
    if (options.includeThumbnails) {
      const MAX_THUMBS = 50;
      const BATCH_SIZE = 10;
      const artworksWithImages = artworks
        .filter((aw) => getImageForPDF(aw))
        .slice(0, MAX_THUMBS);

      for (let i = 0; i < artworksWithImages.length; i += BATCH_SIZE) {
        const batch = artworksWithImages.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (aw) => {
            const imgUrl = getImageForPDF(aw);
            try {
              const resp = await fetch(imgUrl);
              if (!resp.ok) return;
              const blob = await resp.blob();
              const reader = new FileReader();
              const base64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              imagesMap[aw.id] = base64;
            } catch (err) {
              console.error("Erreur chargement miniature:", err);
            }
          })
        );
      }
    }

    // ===== HEADER WHITE-LABEL =====
    let headerYPosition = 20;
    
    // Si white-label activé et logo disponible
    if (enableWhiteLabel && userProfile?.company_logo_url) {
      try {
        // Logo centré, proportions conservées dans une boîte de 70 × 18 mm
        const logo = await loadLogoForPdf(userProfile.company_logo_url, { maxW: 70, maxH: 18 });
        doc.addImage(logo.dataUrl, "PNG", pageWidth / 2 - logo.width / 2, 10, logo.width, logo.height);
        // Ligne de base du titre (22 pt : lettres hautes d'environ 6 mm) : sous le logo, avec un blanc
        headerYPosition = 10 + logo.height + 14;
      } catch (err) {
        console.error("Erreur chargement logo:", err);
      }
    }

    // Titre du rapport
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text(reportName, pageWidth / 2, headerYPosition, { align: "center" });

    // Date et source
    if (options.includeDate) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      
      // Texte différent selon white-label ou non
      let dateStr = "";
      if (enableWhiteLabel) {
        // Si white-label : nom de l'entreprise ou de l'expert
        const companyName = userProfile?.company_display_name || userProfile?.company_name || userProfile?.full_name || "Expert";
        dateStr = `${companyName} – ${new Date().toLocaleDateString("fr-FR")}`;
      } else {
        // Par défaut : marque Artkiv
        dateStr = `Généré via Artkiv – ${new Date().toLocaleDateString("fr-FR")}`;
      }
      
      doc.text(dateStr, pageWidth / 2, headerYPosition + 10, { align: "center" });
    }

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, headerYPosition + 15, pageWidth - margin, headerYPosition + 15);

    let yPosition = headerYPosition + 25;

    // intro
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Œuvres présentes (${artworks.length})`, margin, yPosition);
    yPosition += 10;

    // table layout
    const tableWidth = pageWidth - margin * 2;
    const rowHeight = options.includeThumbnails ? 50 : 18;

    // Largeurs fixes par colonne pour éviter les coupures de texte
    const colWidths = {
      dimensions: 35,
      valeur: 38,
      localisation: 40,
    };

    let dynamicHeaders = [];
    let dynamicColWidths = [];

    if (options.includeSize) {
      dynamicHeaders.push("Dimensions");
      dynamicColWidths.push(colWidths.dimensions);
    }
    if (options.includePrice) {
      dynamicHeaders.push("Valeur estimée");
      dynamicColWidths.push(colWidths.valeur);
    }
    if (options.includeLocation && options.groupBy !== "location") {
      dynamicHeaders.push("Localisation");
      dynamicColWidths.push(colWidths.localisation);
    }

    const totalDynWidth = dynamicColWidths.reduce((s, w) => s + w, 0);
    const mainColWidth = tableWidth - totalDynWidth;

    // Pour compatibilité avec le code existant qui utilise otherColWidth
    const otherColWidth = dynamicColWidths.length > 0
      ? totalDynWidth / dynamicColWidths.length
      : 0;

    const drawTableHeader = () => {
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPosition, tableWidth, rowHeight * 0.5, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);

      doc.text("Œuvre", margin + 5, yPosition + 7);

      let xOffset = margin + mainColWidth;
      dynamicHeaders.forEach((header, idx) => {
        doc.text(header, xOffset + 5, yPosition + 7);
        xOffset += dynamicColWidths[idx] || otherColWidth;
      });

      yPosition += rowHeight * 0.5;
    };

    // tri
    let displayArtworks = [...artworks];
    if (options.sortBy === "name") {
      displayArtworks.sort((a, b) =>
        (a.title || "").localeCompare(b.title || "")
      );
    } else if (options.sortBy === "artist") {
      displayArtworks.sort((a, b) =>
        (a.artist || "").localeCompare(b.artist || "")
      );
    } else if (options.sortBy === "value") {
      displayArtworks.sort(
        (a, b) => (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0)
      );
    } else if (options.sortBy === "acquisitionDate") {
      displayArtworks.sort(
        (a, b) =>
          new Date(a.acquired_at || a.created_at || 0) -
          new Date(b.acquired_at || b.created_at || 0)
      );
    }

    // groupement
    const groups = {};
    if (options.groupBy && options.groupBy !== "none") {
      displayArtworks.forEach((aw) => {
        let key = "";
        if (options.groupBy === "artist")
          key = (aw.artist || "Artiste inconnu").trim();
        if (options.groupBy === "location")
          key = aw.location_name || "Emplacement non spécifié";
        if (options.groupBy === "medium")
          key = aw.medium || "Médium non spécifié";

        if (!groups[key]) groups[key] = [];
        groups[key].push(aw);
      });
    } else {
      groups["_NO_GROUP_"] = displayArtworks;
    }

    // rendu des lignes
    const renderRows = (rows) => {
      for (let i = 0; i < rows.length; i++) {
        const aw = rows[i];

        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
          drawTableHeader();
        }

        if (i % 2 === 1) {
          doc.setFillColor(248, 248, 248);
          doc.rect(margin, yPosition, tableWidth, rowHeight, "F");
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);

        const baseX = margin + 5;
        const baseY = yPosition + 8;
        let textX = baseX;
        let currentY = baseY;

        const thumbBase64 = imagesMap[aw.id];
        const canShowImage = options.includeThumbnails && thumbBase64;

        if (canShowImage) {
          try {
            doc.addImage(thumbBase64, "JPEG", baseX, yPosition + 5, 30, 30);
            textX = baseX + 35;
          } catch (e) {
            console.error("addImage fail:", e);
          }
        }

        // Référence inventaire (en petit, gris) — seulement si différente du titre
        const invNum = aw.inventory_number || "";
        const titleVal = aw.title || "";
        if (invNum && invNum.trim().toLowerCase() !== titleVal.trim().toLowerCase()) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(invNum, textX, currentY);
          currentY += 3.5;
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);
        }

        // Titre
        doc.setFont("helvetica", "bold");
        const titleLines = doc.splitTextToSize(
          aw.title || "Sans titre",
          mainColWidth - (textX - margin) - 5
        );
        doc.text(titleLines, textX, currentY);
        currentY += titleLines.length * 4;

        // Artiste
        doc.setFont("helvetica", "normal");
        if (options.includeArtist) {
          const artistLines = doc.splitTextToSize(
            aw.artist || "-",
            mainColWidth - (textX - margin) - 5
          );
          doc.text(artistLines, textX, currentY);
          currentY += artistLines.length * 4;
        }

        // Medium
        if (options.includeMedium) {
          const mediumLines = doc.splitTextToSize(
            aw.medium || aw.medium_detail || "-",
            mainColWidth - (textX - margin) - 5
          );
          doc.text(mediumLines, textX, currentY);
          currentY += mediumLines.length * 4;
        }

        // colonnes dynamiques
        let dynColIndex = 0;

        // Position Y verticale centrée dans la ligne pour les colonnes dynamiques
        const dynY = yPosition + Math.min(8, rowHeight * 0.4);

        if (options.includeSize) {
          const dimTxt = getDimensionsForPDF(aw);
          const xDyn = margin + mainColWidth + dynamicColWidths.slice(0, dynColIndex).reduce((s,w)=>s+w,0) + 3;
          const colW = dynamicColWidths[dynColIndex] || otherColWidth;
          const dimLines = doc.splitTextToSize(dimTxt || "-", colW - 6);
          doc.text(dimLines, xDyn, dynY);
          dynColIndex++;
        }

        if (options.includePrice) {
          const rt = retainedMap[aw.id];
          const rawVal = (rt && rt.amount != null) ? rt.amount
            : (aw.estimated_value || aw.current_estimated_value || aw.valuation_market_value || aw.value || 0);
          const formatted = rawVal ? formatMoneyEUR(rawVal) : "—";
          const xDyn = margin + mainColWidth + dynamicColWidths.slice(0, dynColIndex).reduce((s,w)=>s+w,0) + 3;
          const colW = dynamicColWidths[dynColIndex] || otherColWidth;
          const priceLines = doc.splitTextToSize(formatted, colW - 6);
          doc.text(priceLines, xDyn, dynY);
          // Source de la valeur retenue (traçabilité) — sous-texte discret.
          if (options.includeValueSource && rt && rt.source_name) {
            const prevSize = doc.getFontSize();
            doc.setFontSize(6.5);
            doc.setTextColor(150, 150, 150);
            doc.text(doc.splitTextToSize(String(rt.source_name), colW - 6), xDyn, dynY + 3.6);
            doc.setFontSize(prevSize);
            doc.setTextColor(0, 0, 0);
          }
          dynColIndex++;
        }

        if (options.includeLocation && options.groupBy !== "location") {
          const xDyn = margin + mainColWidth + dynamicColWidths.slice(0, dynColIndex).reduce((s,w)=>s+w,0) + 3;
          const colW = dynamicColWidths[dynColIndex] || otherColWidth;
          const locLines = doc.splitTextToSize(aw.location_name || "-", colW - 6);
          doc.text(locLines, xDyn, dynY);
          dynColIndex++;
        }

        doc.setDrawColor(230, 230, 230);
        doc.line(
          margin,
          yPosition + rowHeight,
          margin + tableWidth,
          yPosition + rowHeight
        );

        yPosition += rowHeight;
      }
    };

    // groupes
    for (const [groupName, rows] of Object.entries(groups)) {
      if (groupName !== "_NO_GROUP_") {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        let label = groupName;
        if (options.groupBy === "artist") {
          label = `Artiste : ${groupName}`;
        } else if (options.groupBy === "location") {
          label = `Localisation : ${groupName}`;
        } else if (options.groupBy === "medium") {
          label = `Médium : ${groupName}`;
        }

        doc.text(label, margin, yPosition + 8);
        yPosition += 15;
      }

      drawTableHeader();
      renderRows(rows);

      yPosition += 5;
    }

    // totaux globaux (estimation / achat / assurance)
    if (
      (options.includeValueTotal || options.includePurchaseValue) &&
      artworks.length > 0
    ) {
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = 20;
      }

      const totalEstimated = artworks.reduce(
        (sum, aw) => sum + (parseFloat(aw.estimated_value || aw.current_estimated_value || aw.valuation_market_value || aw.value) || 0),
        0
      );

      const totalPurchase = artworks.reduce((sum, aw) => {
        let price = 0;
        if (typeof aw.acquisitionDetails === "string") {
          try {
            price =
              JSON.parse(aw.acquisitionDetails)?.price || 0;
          } catch {
            price = 0;
          }
        } else if (aw.acquisitionDetails?.price) {
          price = aw.acquisitionDetails.price;
        }
        return sum + (parseFloat(price) || 0);
      }, 0);

      const totalInsured = artworks.reduce((sum, aw) => {
        const insured =
          parseFloat(aw.insured_value) ||
          parseFloat(aw.insuredValue) ||
          0;
        return sum + insured;
      }, 0);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      doc.text(
        `Valeur estimée totale : ${formatMoneyEUR(
          totalEstimated
        )}`,
        margin,
        yPosition + 10
      );
      yPosition += 8;

      if (options.includePurchaseValue) {
        doc.text(
          `Valeur d'achat totale : ${formatMoneyEUR(
            totalPurchase
          )}`,
          margin,
          yPosition + 10
        );
        yPosition += 8;
      }

      if (totalInsured > 0) {
        doc.text(
          `Valeur assurée totale : ${formatMoneyEUR(
            totalInsured
          )}`,
          margin,
          yPosition + 10
        );
        yPosition += 8;
      }

      yPosition += 12;
    }
// clause de non-responsabilité / attestation
if (yPosition > pageHeight - 40) {
  doc.addPage();
  yPosition = 20;
}

doc.setFont("helvetica", "normal");
doc.setFontSize(9);
doc.setTextColor(80, 80, 80);

// Texte juridique en plusieurs lignes
const disclaimerLines = doc.splitTextToSize(
  valuationFooter(artworks, "fr") + " " +
  "Les valeurs indiquées et/ou les informations fournies dans ce document sont données à titre purement indicatif et ne constituent pas une expertise officielle. Elles ne sauraient engager la responsabilité d’ARTKIV ni être utilisées comme base unique pour une assurance, une vente, une déclaration fiscale ou successorale. " +
  "Pour toute valorisation opposable (assurance, succession, fiscalité, prêt garanti, etc.), une expertise professionnelle formelle est requise.",
  pageWidth - margin * 2
);

doc.text(disclaimerLines, margin, yPosition + 10);
yPosition += disclaimerLines.length * 5 + 10;

    // footer pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setDrawColor(200, 200, 200);
      doc.line(
        margin,
        pageHeight - 15,
        pageWidth - margin,
        pageHeight - 15
      );

      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "Document confidentiel — Généré via Artkiv",
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );

      doc.text(
        `Page ${i} / ${pageCount}`,
        pageWidth - margin,
        pageHeight - 10
      );
    }

    return doc;
  };


  // ─────────────────────────────────────────────────────────────────────────────
  // ── Helpers PDF partagés ─────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const pdfHeader = (doc, title, pageWidth, margin) => {
    const dateStr = enableWhiteLabel
      ? (userProfile?.company_display_name || userProfile?.company_name || userProfile?.full_name || "Expert") + " – " + new Date().toLocaleDateString("fr-FR")
      : "Généré via Artkiv – " + new Date().toLocaleDateString("fr-FR");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text(title, pageWidth / 2, 22, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(dateStr, pageWidth / 2, 30, { align: "center" });
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, 34, pageWidth - margin, 34);
    return 42;
  };

  const fmtVal = (n) => {
    if (!n) return "—";
    // Intl.NumberFormat FR utilise un espace insécable ( ) que jsPDF ne gère pas bien
    // On formate manuellement avec un espace normal
    const num = Math.round(Number(n));
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " EUR";
  };

  const fmtEff = (aw) => aw?.current_estimated_value || aw?.valuation_market_value || aw?.valuation_consensus || aw?.estimated_value || aw?.value || null;

  const checkPage = (doc, y, pageHeight, margin, needed = 20) => {
    if (y > pageHeight - needed) { doc.addPage(); return margin; }
    return y;
  };

  const sectionHeader = (doc, text, y, pageWidth, margin, bgColor = [240, 236, 229]) => {
    doc.setFillColor(...bgColor);
    doc.rect(margin, y, pageWidth - margin * 2, 10, "F");
    // Barre de couleur à gauche
    const barColor = bgColor[0] > 240 ? [220, 38, 38] : bgColor[2] > 240 ? [3, 105, 161] : [217, 119, 6];
    doc.setFillColor(...barColor);
    doc.rect(margin, y, 3, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text(text.toUpperCase(), margin + 6, y + 6.5);
    return y + 14;
  };


  const columnHeaders = (doc, y, pageWidth, margin, cols) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    let x = margin;
    cols.forEach(({ label, w, align }) => {
      doc.text(label, align === "right" ? x + w : x, y, align === "right" ? { align: "right" } : {});
      x += w;
    });
    doc.setDrawColor(220, 216, 204);
    doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
    return y + 5;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ── Rapport d'évaluation ──────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const generateValuationReport = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    let y = pdfHeader(doc, reportName, pageWidth, margin);

    // ── Totaux globaux ──
    const totalEst  = artworks.reduce((s, a) => s + (Number(fmtEff(a)) || 0), 0);
    const totalLow  = artworks.reduce((s, a) => s + (Number(a.valuation_low  || a.price_low  || 0)), 0);
    const totalHigh = artworks.reduce((s, a) => s + (Number(a.valuation_high || a.price_high || 0)), 0);
    const totalAcq  = artworks.reduce((s, a) => s + (Number(a.acquisition_price || 0)), 0);
    const withVal   = artworks.filter(a => fmtEff(a) > 0).length;

    // Bandeau récap
    doc.setFillColor(26, 26, 26);
    doc.rect(margin, y, pageWidth - margin * 2, 28, "F");
    const summaryItems = [
      { label: "Valeur estimée consensus", val: fmtVal(totalEst), main: true },
      { label: "Fourchette basse", val: totalLow > 0 ? fmtVal(totalLow) : "—", main: false },
      { label: "Fourchette haute", val: totalHigh > 0 ? fmtVal(totalHigh) : "—", main: false },
      { label: "Valeur d'acquisition", val: totalAcq > 0 ? fmtVal(totalAcq) : "—", main: false },
      { label: "Œuvres valorisées", val: withVal + " / " + artworks.length, main: false },
    ];
    const iW2 = (pageWidth - margin * 2) / summaryItems.length;
    summaryItems.forEach((item, i) => {
      const x = margin + i * iW2 + 3;
      doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(180, 180, 180);
      doc.text(item.label, x, y + 7);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(item.main ? 11 : 9);
      doc.setTextColor(item.main ? 201 : 255, item.main ? 169 : 255, item.main ? 97 : 255);
      doc.text(item.val, x, y + 18);
    });
    y += 34;

    // ── Avertissement légal ──
    doc.setFont("helvetica", "italic"); doc.setFontSize(6.5); doc.setTextColor(140, 140, 140);
    const disclaimer = valuationFooter(artworks, "fr") + " Les valeurs indiquées sont données à titre indicatif et ne constituent pas une expertise opposable. Pour toute valorisation formelle (assurance, succession, fiscalité), une expertise professionnelle est requise.";
    const discLines = doc.splitTextToSize(disclaimer, pageWidth - margin * 2);
    doc.text(discLines, margin, y);
    y += discLines.length * 3.5 + 6;

    // ── Groupement par artiste ──
    const byArtist = {};
    artworks.forEach(aw => {
      const artist = aw.artist || "Artiste inconnu";
      if (!byArtist[artist]) byArtist[artist] = [];
      byArtist[artist].push(aw);
    });

    const artistsSorted = Object.keys(byArtist).sort((a, b) => {
      // Trier par valeur totale décroissante
      const va = byArtist[a].reduce((s, x) => s + (Number(fmtEff(x)) || 0), 0);
      const vb = byArtist[b].reduce((s, x) => s + (Number(fmtEff(x)) || 0), 0);
      return vb - va;
    });

    for (const artist of artistsSorted) {
      const works = byArtist[artist];
      const artistTotal = works.reduce((s, a) => s + (Number(fmtEff(a)) || 0), 0);
      const artistLow   = works.reduce((s, a) => s + (Number(a.valuation_low  || a.price_low  || 0)), 0);
      const artistHigh  = works.reduce((s, a) => s + (Number(a.valuation_high || a.price_high || 0)), 0);

      y = checkPage(doc, y, pageHeight, margin, 30);

      // En-tête artiste
      doc.setFillColor(248, 247, 244);
      doc.rect(margin, y, pageWidth - margin * 2, 11, "F");
      doc.setFillColor(201, 169, 97); // gold
      doc.rect(margin, y, 3, 11, "F");

      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(26, 26, 26);
      doc.text(artist, margin + 6, y + 7);

      // Total artiste à droite
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      const artistSummary = works.length + " œuvre" + (works.length > 1 ? "s" : "") +
        (artistTotal > 0 ? "  ·  " + fmtVal(artistTotal) : "  ·  non valorisé") +
        (artistLow > 0 && artistHigh > 0 ? "  (" + fmtVal(artistLow) + " – " + fmtVal(artistHigh) + ")" : "");
      doc.text(artistSummary, pageWidth - margin - 3, y + 7, { align: "right" });
      y += 13;

      // En-têtes colonnes
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(160, 160, 160);
      doc.text("INV.",     margin,       y);
      doc.text("TITRE",    margin + 18,  y);
      doc.text("MÉDIUM",   margin + 78,  y);
      doc.text("DIM.",     margin + 118, y);
      doc.text("ANNÉE",    margin + 138, y);
      doc.text("FOURCHETTE",  margin + 152, y);
      doc.text("VALEUR EST.", margin + 178, y, { align: "right" });
      doc.setDrawColor(220, 216, 204);
      doc.line(margin, y + 1.5, pageWidth - margin, y + 1.5);
      y += 5;

      for (const aw of works) {
        y = checkPage(doc, y, pageHeight, margin, 8);

        const inv    = aw.inventory_number || aw.lot_copy_number || "—";
        const rawT   = aw.title || "Sans titre";
        const title  = (rawT === inv ? "Sans titre" : rawT);
        const med    = (aw.medium_original || aw.medium || "—").slice(0, 25);
        const dims   = (() => {
          const h = aw.dimensions_height || aw.height;
          const w = aw.dimensions_width  || aw.width;
          if (h && w) return Math.round(h) + "×" + Math.round(w);
          return aw.dimensions?.replace(/\s*x\s*/gi, "×").slice(0, 15) || "—";
        })();
        const year   = aw.year || "—";
        const val    = fmtEff(aw) ? fmtVal(fmtEff(aw)) : "—";
        const low    = aw.valuation_low  || aw.price_low;
        const high   = aw.valuation_high || aw.price_high;
        const range  = (low && high) ? fmtVal(low) + "–" + fmtVal(high) : "—";
        const method = aw.valuation_method ? aw.valuation_method.replace(/_/g, " ").slice(0, 12) : "";

        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
        doc.text(inv, margin, y);
        doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
        const titleLines = doc.splitTextToSize(title, 57);
        doc.text(titleLines, margin + 18, y);
        doc.text(med,  margin + 78,  y);
        doc.text(dims, margin + 118, y);
        doc.text(year, margin + 138, y);

        // Fourchette en gris
        doc.setFontSize(6.5); doc.setTextColor(120, 120, 120);
        const rangeLines = doc.splitTextToSize(range, 23);
        doc.text(rangeLines, margin + 152, y);

        // Valeur consensus en noir/gras
        doc.setFont("helvetica", "bold"); doc.setFontSize(8);
        doc.setTextColor(val === "—" ? 180 : 0, 0, 0);
        doc.text(val, pageWidth - margin, y, { align: "right" });

        // Méthode en tout petit sous la valeur
        if (method) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(5.5); doc.setTextColor(160, 160, 160);
          doc.text(method.toUpperCase(), pageWidth - margin, y + 3.5, { align: "right" });
        }

        doc.setDrawColor(240, 237, 232);
        doc.line(margin, y + titleLines.length * 4 + 0.5, pageWidth - margin, y + titleLines.length * 4 + 0.5);
        y += titleLines.length * 5 + 1.5;
      }

      // Sous-total artiste
      if (artistTotal > 0) {
        y = checkPage(doc, y, pageHeight, margin, 10);
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
        doc.text("Total " + artist + " : " + fmtVal(artistTotal), pageWidth - margin, y, { align: "right" });
        y += 8;
      } else {
        y += 4;
      }
    }

    // ── Total général ──
    y = checkPage(doc, y, pageHeight, margin, 20);
    doc.setDrawColor(26, 26, 26);
    doc.line(margin, y, pageWidth - margin, y);
    y += 7;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(26, 26, 26);
    doc.text("VALEUR TOTALE ESTIMÉE : " + fmtVal(totalEst), margin, y);
    if (totalLow > 0 && totalHigh > 0) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      doc.text("Fourchette : " + fmtVal(totalLow) + " – " + fmtVal(totalHigh), margin, y + 6);
    }
    if (totalAcq > 0) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      doc.text("Valeur d'acquisition totale : " + fmtVal(totalAcq), margin, y + 12);
    }

    // ── Pied de page ──
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160, 160, 160);
      doc.text("Document confidentiel — Rapport d'évaluation", margin, pageHeight - 8);
      doc.text("Page " + i + " / " + pageCount, pageWidth - margin, pageHeight - 8, { align: "right" });
    }
    return doc;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ── Rapport d'état des œuvres ─────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const generateConditionReport = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    let y = pdfHeader(doc, reportName, pageWidth, margin);

    const total       = artworks.length;
    // "Non assurée" = a une valeur estimée mais pas de police d'assurance renseignée
    const notInsured  = artworks.filter(a => 
      (!a.insurance_status || a.insurance_status === "not_insured") && 
      (Number(fmtEff(a)) > 0)
    );
    const onLoan      = artworks.filter(a => a.loan_status === "on_loan" || a.availability === "on_loan");
    const noLocation  = artworks.filter(a => !a.location_id);
    // "Documentation incomplète" = pas de facture, certificat ou document prouvant l'acquisition
    const docInc      = artworks.filter(a => !hasDocumentForCompleteness(a) && Number(fmtEff(a)) > 0);
    const highRisk    = artworks.filter(a => a.storage_risk === "élevé");
    const condBad     = artworks.filter(a => ["poor", "fair", "damaged"].includes(a.condition));

    // ── Bandeau synthèse ──
    const synthItems = [
      { label: "Non assurées",          count: notInsured.length,  color: notInsured.length  > 0 ? [220, 38, 38]  : [22, 163, 74] },
      { label: "En prêt",               count: onLoan.length,      color: onLoan.length      > 0 ? [3, 105, 161]  : [22, 163, 74] },
      { label: "Sans localisation",     count: noLocation.length,  color: noLocation.length  > 0 ? [217, 119, 6]  : [22, 163, 74] },
      { label: "Doc. incomplète",       count: docInc.length,      color: docInc.length      > 0 ? [217, 119, 6]  : [22, 163, 74] },
      { label: "Risque stockage élevé", count: highRisk.length,    color: highRisk.length    > 0 ? [220, 38, 38]  : [22, 163, 74] },
      { label: "État dégradé",          count: condBad.length,     color: condBad.length     > 0 ? [220, 38, 38]  : [22, 163, 74] },
    ];

    doc.setFillColor(248, 247, 244);
    doc.rect(margin, y, pageWidth - margin * 2, 26, "F");
    doc.setDrawColor(220, 216, 204);
    doc.rect(margin, y, pageWidth - margin * 2, 26, "S");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(100, 100, 100);
    doc.text("SYNTHÈSE — " + total + " OEUVRES", margin + 3, y + 5);

    const iW = (pageWidth - margin * 2 - 6) / synthItems.length;
    synthItems.forEach((item, i) => {
      const x = margin + 3 + i * iW;
      doc.setTextColor(...item.color);
      doc.setFont("helvetica", "bold"); doc.setFontSize(14);
      doc.text(String(item.count), x, y + 16);
      doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(100, 100, 100);
      const lbl = doc.splitTextToSize(item.label, iW - 2);
      doc.text(lbl, x, y + 21);
    });
    y += 32;

    // ── Sections par problème ──
    const COLS = [
      { label: "INV.", w: 20 }, { label: "TITRE — ARTISTE", w: 95 },
      { label: "MÉDIUM", w: 40 }, { label: "VALEUR", w: 25, align: "right" },
    ];

    const renderSection = (title, list, bgColor) => {
      if (list.length === 0) return;
      y = checkPage(doc, y, pageHeight, margin, 30);
      y = sectionHeader(doc, title + " (" + list.length + ")", y, pageWidth, margin, bgColor);
      y = columnHeaders(doc, y, pageWidth, margin, COLS);
      list.forEach(aw => {
        y = checkPage(doc, y, pageHeight, margin, 8);
        const inv   = aw.inventory_number || aw.lot_copy_number || "—";
        // Éviter de répéter le numéro d'inventaire si le titre = numéro d'inventaire
        const rawTitle = aw.title || "Sans titre";
        const cleanTitle = (rawTitle === inv) ? "Sans titre" : rawTitle;
        const title2 = cleanTitle + " — " + (aw.artist || "—");
        const med   = (aw.medium_original || aw.medium || "—").slice(0, 35);
        const val   = fmtVal(fmtEff(aw));
        doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(0, 0, 0);
        doc.text(inv, margin, y);
        doc.setFont("helvetica", "normal");
        const tLines = doc.splitTextToSize(title2, 92);
        doc.text(tLines, margin + 20, y);
        doc.text(med, margin + 115, y);
        doc.text(val, margin + 178, y, { align: "right" });
        // Ligne séparatrice légère
        doc.setDrawColor(240, 237, 232);
        doc.line(margin, y + 0.5, pageWidth - margin, y + 0.5);
        y += tLines.length * 5 + 2;
      });
      y += 6;
    };

    const pct = (list) => list.length / total;
    // N'afficher une section que si elle contient des œuvres ET < 95% du total
    // (sinon ça n'apporte rien : trop générique)
    if (notInsured.length > 0 && pct(notInsured) < 0.95)
      renderSection("OEUVRES NON ASSURÉES (avec valeur estimée)", notInsured, [255, 235, 235]);
    if (highRisk.length > 0)
      renderSection("RISQUE DE STOCKAGE ÉLEVÉ", highRisk, [255, 242, 235]);
    if (noLocation.length > 0)
      renderSection("SANS LOCALISATION RENSEIGNÉE", noLocation, [255, 251, 230]);
    if (onLoan.length > 0)
      renderSection("EN PRÊT", onLoan, [224, 242, 254]);
    if (condBad.length > 0)
      renderSection("ÉTAT DE CONSERVATION DÉGRADÉ", condBad, [255, 235, 235]);
    if (docInc.length > 0 && pct(docInc) < 0.95)
      renderSection("SANS DOCUMENT D'ACQUISITION (avec valeur estimée)", docInc, [255, 251, 230]);

    // Si toutes les sections sont vides ou filtrées, afficher un message positif
    const hasAlerts = (notInsured.length > 0 && pct(notInsured) < 0.95) ||
      highRisk.length > 0 || noLocation.length > 0 || onLoan.length > 0 ||
      condBad.length > 0 || (docInc.length > 0 && pct(docInc) < 0.95);
    if (!hasAlerts) {
      y = checkPage(doc, y, pageHeight, margin, 30);
      doc.setFillColor(240, 253, 244);
      doc.rect(margin, y, pageWidth - margin * 2, 20, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(22, 163, 74);
      doc.text("Aucune alerte détectée", margin + 10, y + 9);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
      doc.text("Toutes les œuvres avec valeur estimée sont localisées et documentées.", margin + 10, y + 15);
    }

    // ── Pied de page ──
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160, 160, 160);
      doc.text("Document confidentiel", margin, pageHeight - 8);
      doc.text("Page " + i + " / " + pageCount, pageWidth - margin, pageHeight - 8, { align: "right" });
    }
    return doc;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ── Rapport pour assurance ────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const generateInsuranceReport = () => {
    const doc = new jsPDF("l", "mm", "a4"); // paysage pour les colonnes
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    let y = pdfHeader(doc, reportName, pageWidth, margin);

    // Charger les données insurance depuis le state
    // On groupe par artwork — chaque ligne = 1 œuvre avec sa valeur assurée si elle existe
    const totalEstimated = artworks.reduce((s, a) => s + (Number(fmtEff(a)) || 0), 0);

    // ── Synthèse ──
    const notInsured = artworks.filter(a => !a.insurance_status || a.insurance_status === "not_insured");
    doc.setFillColor(248, 247, 244);
    doc.rect(margin, y, pageWidth - margin * 2, 18, "F");
    doc.setDrawColor(220, 216, 204);
    doc.rect(margin, y, pageWidth - margin * 2, 18, "S");

    const synthCols = [
      { label: "Total collection estimé", val: fmtVal(totalEstimated) },
      { label: "Œuvres sans assurance renseignée", val: String(notInsured.length) + " / " + artworks.length },
      { label: "Valeur non assurée (estimation)", val: fmtVal(notInsured.reduce((s, a) => s + (Number(fmtEff(a)) || 0), 0)) },
    ];
    const cW = (pageWidth - margin * 2) / synthCols.length;
    synthCols.forEach((c, i) => {
      const x = margin + i * cW + 4;
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(100, 100, 100);
      doc.text(c.label, x, y + 6);
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0, 0, 0);
      doc.text(c.val, x, y + 14);
    });
    y += 24;

    // ── En-tête colonnes ──
    const COLS = [
      { label: "INV.", w: 18 }, { label: "TITRE", w: 60 }, { label: "ARTISTE", w: 40 },
      { label: "MÉDIUM", w: 35 }, { label: "DIMENSIONS", w: 28 },
      { label: "VALEUR ESTIMÉE", w: 30, align: "right" }, { label: "LOCALISATION", w: 40 },
      { label: "STATUT ASSUR.", w: 25 },
    ];

    y = columnHeaders(doc, y, pageWidth, margin, COLS);

    artworks.forEach(aw => {
      y = checkPage(doc, y, pageHeight, margin, 8);
      const inv    = aw.inventory_number || aw.lot_copy_number || "—";
      const title  = (aw.title || "Sans titre").slice(0, 35);
      const artist = (aw.artist || "—").slice(0, 22);
      const med    = (aw.medium_original || aw.medium || "—").slice(0, 20);
      const dims   = aw.dimensions || ([aw.dimensions_height, aw.dimensions_width].filter(Boolean).join(" x ") + " cm") || "—";
      const val    = fmtVal(fmtEff(aw));
      const loc    = (aw.location_name || "—").slice(0, 22);
      const ins    = !aw.insurance_status || aw.insurance_status === "not_insured" ? "NON ASSURÉE" : "Assurée";

      const isAlert = !aw.insurance_status || aw.insurance_status === "not_insured";
      doc.setTextColor(isAlert ? 180 : 0, 0, 0);

      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
      doc.text(inv, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(title, margin + 18, y);
      doc.text(artist, margin + 78, y);
      doc.text(med, margin + 118, y);
      doc.text(dims.slice(0, 15), margin + 153, y);
      doc.text(val, margin + 210, y, { align: "right" });
      doc.text(loc, margin + 213, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(isAlert ? 220 : 22, isAlert ? 38 : 163, isAlert ? 38 : 74);
      doc.text(ins, margin + 253, y);
      doc.setTextColor(0, 0, 0);
      y += 5.5;
    });

    // ── Total ──
    y = checkPage(doc, y, pageHeight, margin, 15);
    doc.setDrawColor(0, 0, 0);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(0, 0, 0);
    doc.text("Valeur totale estimée de la collection : " + fmtVal(totalEstimated), margin, y);

    // ── Pied de page ──
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160, 160, 160);
      doc.text("Document confidentiel — Rapport pour assurance", margin, pageHeight - 8);
      doc.text("Page " + i + " / " + pageCount, pageWidth - margin, pageHeight - 8, { align: "right" });
    }
    return doc;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ── Rapport par localisation ──────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────

  const generateLocationReport = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    let y = pdfHeader(doc, reportName, pageWidth, margin);

    // Grouper par location_name
    const groups = {};
    artworks.forEach(aw => {
      const key = aw.location_name || "Localisation non renseignée";
      if (!groups[key]) groups[key] = { artworks: [], locData: aw.location_data };
      groups[key].artworks.push(aw);
    });

    const COLS = [
      { label: "INV.", w: 20 }, { label: "TITRE — ARTISTE", w: 90 },
      { label: "MÉDIUM", w: 40 }, { label: "VALEUR EST.", w: 30, align: "right" },
    ];

    const RISK_STYLE = {
      faible: { label: "Risque faible", color: [22, 163, 74] },
      moyen:  { label: "Risque moyen",  color: [217, 119, 6] },
      élevé:  { label: "Risque élevé",  color: [220, 38, 38] },
    };

    Object.entries(groups).forEach(([locName, { artworks: locArtworks, locData }]) => {
      y = checkPage(doc, y, pageHeight, margin, 40);

      // ── En-tête lieu ──
      doc.setFillColor(26, 26, 26);
      doc.rect(margin, y, pageWidth - margin * 2, 9, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(255, 255, 255);
      doc.text(locName + "  (" + locArtworks.length + " œuvre" + (locArtworks.length > 1 ? "s" : "") + ")", margin + 3, y + 6);

      // Valeur totale du lieu
      const locTotal = locArtworks.reduce((s, a) => s + (Number(fmtEff(a)) || 0), 0);
      if (locTotal > 0) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(8);
        doc.text(fmtVal(locTotal), pageWidth - margin - 3, y + 6, { align: "right" });
      }
      y += 11;

      // ── Infos de conservation ──
      if (locData) {
        const parts = [];
        if (locData.address)     parts.push(locData.address);
        if (locData.temperature) parts.push("T° : " + locData.temperature);
        if (locData.humidity)    parts.push("HR : " + locData.humidity);
        if (locData.light)       parts.push("Lumière : " + locData.light);
        if (locData.security_measures?.length) parts.push("Sécurité : " + locData.security_measures.join(", "));
        if (locData.storage_notes) parts.push("Note : " + locData.storage_notes);

        if (parts.length > 0) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(80, 80, 80);
          const infoText = parts.join("  ·  ");
          const infoLines = doc.splitTextToSize(infoText, pageWidth - margin * 2 - 6);
          doc.text(infoLines, margin, y + 3);
          y += infoLines.length * 3.5 + 2;
        }

        // Badge risque
        if (locData.storage_risk && RISK_STYLE[locData.storage_risk]) {
          const rs = RISK_STYLE[locData.storage_risk];
          doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...rs.color);
          doc.text("⚑ " + rs.label.toUpperCase(), margin, y + 3);
          y += 6;
        }
      }
      y += 2;

      // ── Colonnes + lignes œuvres ──
      y = columnHeaders(doc, y, pageWidth, margin, COLS);

      locArtworks.forEach(aw => {
        y = checkPage(doc, y, pageHeight, margin, 8);
        const inv    = aw.inventory_number || aw.lot_copy_number || "—";
        // Éviter de répéter le numéro d'inventaire si le titre = numéro d'inventaire
        const rawTitle = aw.title || "Sans titre";
        const cleanTitle = (rawTitle === inv) ? "Sans titre" : rawTitle;
        const title2 = cleanTitle + " — " + (aw.artist || "—");
        const med    = (aw.medium_original || aw.medium || "—").slice(0, 30);
        const val    = fmtVal(fmtEff(aw));

        doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(0, 0, 0);
        doc.text(inv, margin, y);
        doc.setFont("helvetica", "normal");
        const tLines = doc.splitTextToSize(title2, 87);
        doc.text(tLines, margin + 20, y);
        doc.text(med, margin + 110, y);
        doc.text(val, pageWidth - margin, y, { align: "right" });
        y += tLines.length * 4.2 + 1.5;
      });

      // Sous-total
      if (locTotal > 0) {
        y = checkPage(doc, y, pageHeight, margin, 10);
        doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(100, 100, 100);
        doc.text("Sous-total : " + fmtVal(locTotal), pageWidth - margin, y + 2, { align: "right" });
        y += 8;
      } else {
        y += 6;
      }
    });

    // ── Pied de page ──
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160, 160, 160);
      doc.text("Document confidentiel", margin, pageHeight - 8);
      doc.text("Page " + i + " / " + pageCount, pageWidth - margin, pageHeight - 8, { align: "right" });
    }
    return doc;
  };

  // ── Rapport de complétude ────────────────────────────────────────────────────
  const generateCompletudeReport = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const tableW = pageWidth - margin * 2;
    let y = 20;

    const gold  = [180, 150, 80];
    const red   = [180, 40, 40];
    const green = [40, 140, 80];
    const gray  = [120, 120, 120];
    const black = [26, 26, 26];
    const cream = [249, 247, 244];

    const checkPage = (needed = 16) => {
      if (y + needed > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
    };

    // ── Définir les checks actifs ──
    const checks = [
      { key: "checkImage",          label: "Image principale",    test: hasImageForCompleteness },
      { key: "checkArtist",         label: "Nom de l'artiste",    test: hasArtistForCompleteness },
      { key: "checkDimensions",     label: "Dimensions",          test: a => !!(getDimensionsForPDF(a) && getDimensionsForPDF(a) !== "-") },
      { key: "checkMedium",         label: "Technique / médium",  test: hasMediumForCompleteness },
      { key: "checkYear",           label: "Année de création",   test: a => !!(a.year || a.creationDate) },
      { key: "checkEstimatedValue", label: "Valeur estimée",      test: a => !!(parseFloat(a.estimated_value || a.current_estimated_value || a.valuation_market_value || a.value) > 0) },
      { key: "checkDocuments",      label: "Document (facture / certificat)", test: hasDocumentForCompleteness },
      { key: "checkInventoryNumber",label: "Réf. inventaire",     test: a => !!(a.inventory_number && a.inventory_number.trim()) },
      { key: "checkDescription",    label: "Description",         test: a => !!(a.description && a.description.trim().length > 10) },
      { key: "checkAcquisitionPrice",label: "Prix d'achat",       test: a => !!(parseFloat(a.acquisition_price || a.price || a.price_low) > 0) },
    ].filter(c => options[c.key]);

    // ── Score de complétude par œuvre ──
    const scored = artworks.map(a => {
      const missing = checks.filter(c => !c.test(a)).map(c => c.label);
      const score = Math.round(((checks.length - missing.length) / checks.length) * 100);
      return { ...a, _missing: missing, _score: score };
    });

    // Trier : moins complet en premier, puis référence inventaire ascendante
    scored.sort((a, b) => (a._score - b._score) || inventorySortKey(a).localeCompare(inventorySortKey(b), "fr-FR", { numeric: true, sensitivity: "base" }));

    const totalIncomplete = scored.filter(a => a._missing.length > 0).length;
    const avgScore = scored.length > 0
      ? Math.round(scored.reduce((s, a) => s + a._score, 0) / scored.length)
      : 0;

    // ── Page de garde ──
    doc.setFillColor(...gold);
    doc.rect(0, 0, pageWidth, 3, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...black);
    doc.text("Rapport de complétude", margin, y + 10);
    y += 14;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...gray);
    doc.text(`Données manquantes pour valorisation — ${new Date().toLocaleDateString("fr-FR")}`, margin, y);
    y += 12;

    // Bandeau synthèse
    doc.setFillColor(26, 26, 26);
    doc.rect(margin, y, tableW, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(`${artworks.length} œuvres analysées`, margin + 5, y + 8);
    doc.text(`${totalIncomplete} œuvres incomplètes (${Math.round(totalIncomplete / artworks.length * 100)}%)`, margin + 5, y + 16);
    doc.setTextColor(...gold);
    doc.setFontSize(16);
    doc.text(`Complétude moyenne : ${avgScore}%`, pageWidth - margin - 5, y + 12, { align: "right" });
    y += 28;

    // Champs vérifiés
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    doc.text("Champs vérifiés : " + checks.map(c => c.label).join("  ·  "), margin, y);
    y += 10;

    // Barre de progression globale
    const barW = tableW;
    doc.setFillColor(230, 230, 225);
    doc.rect(margin, y, barW, 5, "F");
    doc.setFillColor(...gold);
    doc.rect(margin, y, barW * avgScore / 100, 5, "F");
    y += 12;

    // ── Regroupement par artiste ──
    const byArtist = {};
    scored.forEach(a => {
      const k = (a.artist || "Artiste inconnu").trim();
      if (!byArtist[k]) byArtist[k] = [];
      byArtist[k].push(a);
    });

    // Trier artistes par score moyen croissant
    const sortedArtists = Object.entries(byArtist).sort(([, aArr], [, bArr]) => {
      const avgA = aArr.reduce((s, x) => s + x._score, 0) / aArr.length;
      const avgB = bArr.reduce((s, x) => s + x._score, 0) / bArr.length;
      return avgA - avgB;
    });

    for (const [artistName, rows] of sortedArtists) {
      const incompleteRows = rows.filter(a => a._missing.length > 0);
      if (incompleteRows.length === 0) continue; // skip artistes complets

      const artistScore = Math.round(rows.reduce((s, a) => s + a._score, 0) / rows.length);

      checkPage(20);

      // En-tête artiste
      doc.setFillColor(...cream);
      doc.rect(margin, y, tableW, 12, "F");
      doc.setFillColor(...gold);
      doc.rect(margin, y, 2, 12, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...black);
      doc.text(artistName, margin + 5, y + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...gray);
      doc.text(`${incompleteRows.length} / ${rows.length} œuvres incomplètes — score moyen ${artistScore}%`, margin + 5, y + 10);
      y += 16;

      for (const a of incompleteRows) {
        checkPage(14);

        const titleStr = [a.inventory_number, a.title].filter(Boolean).join(" — ") || "Sans titre";
        const scoreColor = a._score < 50 ? red : a._score < 75 ? [180, 100, 0] : green;

        // Ligne œuvre
        doc.setFillColor(252, 252, 250);
        doc.rect(margin, y, tableW, 11, "F");

        // Badge score
        doc.setFillColor(...scoreColor);
        doc.roundedRect(margin + tableW - 28, y + 1.5, 26, 8, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text(`${a._score}%`, margin + tableW - 15, y + 7, { align: "center" });

        // Titre
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...black);
        const titleLines = doc.splitTextToSize(titleStr, tableW - 36);
        doc.text(titleLines[0], margin + 3, y + 5);

        // Champs manquants en rouge
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...red);
        doc.text("Manquant : " + a._missing.join("  ·  "), margin + 3, y + 9.5);

        doc.setDrawColor(235, 232, 228);
        doc.line(margin, y + 11, margin + tableW, y + 11);
        y += 12;
      }
      y += 4;
    }

    // ── Résumé par champ manquant ──
    checkPage(40);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...black);
    doc.text("Synthèse par champ", margin, y);
    y += 8;

    checks.forEach(c => {
      const missing = scored.filter(a => !c.test(a)).length;
      const pct = Math.round(missing / scored.length * 100);
      const barColor = pct > 50 ? red : pct > 20 ? [180, 100, 0] : green;

      checkPage(10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...black);
      doc.text(c.label, margin, y + 4);
      doc.setTextColor(...gray);
      doc.text(`${missing} œuvres`, margin + 55, y + 4);

      // Barre
      doc.setFillColor(230, 230, 225);
      doc.rect(margin + 80, y, tableW - 100, 5, "F");
      doc.setFillColor(...barColor);
      doc.rect(margin + 80, y, (tableW - 100) * pct / 100, 5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...barColor);
      doc.text(`${pct}%`, margin + tableW - 15, y + 4);
      y += 10;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFillColor(...gold);
      doc.rect(0, pageHeight - 3, pageWidth, 3, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...gray);
      doc.text("Document confidentiel — Généré via Artkiv", pageWidth / 2, pageHeight - 6, { align: "center" });
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 6);
    }

    return doc;
  };

  // handleGenerate avec gestion d'erreurs propre
  const handleGenerate = async () => {
    setIsLoading(true);
    setErrorMsg(null);

    try {
      if (!clientId) {
        throw new Error("Client non authentifié.");
      }

      const { data: clientExists, error: clientError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", clientId)
        .maybeSingle();

      if (clientError) {
        throw new Error(
          "Erreur lors de la vérification du client: " +
            clientError.message
        );
      }
      if (!clientExists) {
        throw new Error(
          "Client introuvable dans la base. Impossible de créer le rapport."
        );
      }

      let doc;
      if (reportType === "completude") {
        doc = generateCompletudeReport();
      } else if (reportType === "valuation") {
        doc = generateValuationReport();
      } else if (reportType === "condition") {
        doc = generateConditionReport();
      } else if (reportType === "insurance") {
        doc = generateInsuranceReport();
      } else if (reportType === "location") {
        doc = generateLocationReport();
      } else {
        doc = await generatePDF();
      }
      if (!doc) {
        throw new Error(
          "La génération du rapport a échoué. Aucun document n'a été produit."
        );
      }

      // téléchargement local immédiat
      doc.save(`${reportName.replace(/\s+/g, "_")}.pdf`);

      // upload Supabase
      const pdfBlob = doc.output("blob");
      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error("Le fichier PDF généré est vide.");
      }

      const timestamp = Date.now();
      const fileName = `rapport_${reportType}_${timestamp}.pdf`;
      const filePath = `reports/${clientId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, pdfBlob, {
          contentType: "application/pdf",
          upsert: true,
        });

      if (uploadError) {
        throw new Error(
          `Erreur lors de l'upload du fichier: ${uploadError.message}`
        );
      }

      const { data: urlData } = await supabase.storage
        .from("documents")
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error(
          "Impossible d'obtenir l'URL publique du fichier."
        );
      }

      const { error: reportError } = await supabase
        .from("reports")
        .insert({
          title: reportName,
          type: reportType,
          options: options,
          file_url: urlData.publicUrl,
          client_id: clientId,
          collection_id: selectedCollectionId || id || null,
          created_at: new Date().toISOString(),
        });

      if (reportError) {
        throw new Error(
          `Erreur lors de l'enregistrement du rapport: ${reportError.message}`
        );
      }

      navigate("/reports");
    } catch (err) {
      console.error("Erreur lors de la génération:", err);
      setErrorMsg(
        err?.message ||
          "Une erreur inconnue est survenue pendant la génération."
      );

      try {
        if (clientId) {
          await supabase.from("logs").insert({
            user_id: clientId,
            context: "report_generation",
            message:
              err?.message ||
              JSON.stringify(err) ||
              "Erreur non sérialisable",
            created_at: new Date().toISOString(),
          });
        }
      } catch (logErr) {
        console.warn("Échec log erreur (non bloquant):", logErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== RENDER ======================
  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <div className="text-right mb-6">
        <Link
          to="/reports"
          className="inline-block px-4 py-2 border border-gray-300 rounded-md text-gray-800 bg-white hover:bg-gray-50 shadow-sm"
        >
          📄 {tr("actions.viewExisting")}
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-8 text-gray-900">
        {tr("title")}
      </h1>

      {errorMsg && (
        <div className="mb-6 p-4 border border-red-300 bg-red-50 text-red-700 rounded-md">
          <strong>{tr("error")} :</strong> {errorMsg}
        </div>
      )}

      {/* TYPE + NOM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {tr("fields.reportType")}
          </label>
          <select
            value={reportType}
            onChange={(e) => handleReportTypeChange(e.target.value)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          >
            {reportTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {tr("fields.reportName")}
          </label>
          <input
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
          />
        </div>
      </div>

      {/* SÉLECTION COLLECTIONNEUR (expert seulement) + COLLECTION */}
      <div className="bg-white border border-[#E8E8E8] rounded-lg p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 rounded" style={{ background: "#c9a961" }} />
          <h2 className="text-base font-medium text-[#1A1A1A]">
            {isAdvisor ? tr("selection.advisorTitle") : tr("selection.collectionTitle")}
          </h2>
        </div>

        <div className={`grid gap-4 ${isAdvisor ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>

          {/* Collectionneur — expert uniquement */}
          {isAdvisor && (
            <div>
              <label className="block text-sm font-medium text-[#555] mb-1.5">
                {tr("selection.collector")}
              </label>
              <select
                value={selectedCollectorId}
                onChange={(e) => {
                  setSelectedCollectorId(e.target.value);
                  setSelectedCollectionId(""); // reset collection quand on change de collectionneur
                }}
                className="block w-full border border-[#E8E8E8] rounded-md py-2 px-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#C9A961]"
              >
                <option value="">{tr("selection.allCollectors")}</option>
                {collectors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.company_name || c.id}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[#999] mt-1">
                {tr("selection.sharedCollectors", { count: collectors.length })}
              </p>
            </div>
          )}

          {/* Collection */}
          <div>
            <label className="block text-sm font-medium text-[#555] mb-1.5">
              {tr("selection.collection")}
            </label>
            <select
              value={selectedCollectionId}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              className="block w-full border border-[#E8E8E8] rounded-md py-2 px-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#C9A961]"
            >
              <option value="">{tr("selection.allCollections")}</option>
              {(isAdvisor
                ? ownCollections.filter(c => !selectedCollectorId || c.client_id === selectedCollectorId)
                : ownCollections
              ).map((c) => (
                <option key={c.id} value={c.id}>{c.title || tr("selection.untitledCollection")}</option>
              ))}
            </select>
            <p className="text-xs text-[#999] mt-1">
              {tr(selectedCollectionId ? "selection.loadedInCollection" : "selection.loadedTotal", { count: artworks.length })}
            </p>
          </div>
        </div>
      </div>

      {/* AVERTISSEMENT GRANDES COLLECTIONS */}
      {artworks.length > 50 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">
              {tr("largeCollection.title", { count: artworks.length })}
            </p>
            <p>
              {tr("largeCollection.description")}
            </p>
            {options.includeThumbnails && (
              <p className="mt-1 text-amber-700">
                {tr("largeCollection.thumbnails")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* OPTIONS COMPLÉTUDE — affichées seulement pour ce type */}
      {reportType === "completude" && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-2 h-8 rounded" style={{ background: "#c9a961" }} />
            <div>
              <h2 className="text-lg font-medium text-gray-900">{tr("completeness.title")}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{tr("completeness.description")}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: "checkImage",           label: tr("data.image"),             essential: true },
              { key: "checkArtist",          label: tr("data.artist"),            essential: true },
              { key: "checkDimensions",      label: tr("data.dimensions"),        essential: true },
              { key: "checkMedium",          label: tr("data.medium"),            essential: true },
              { key: "checkYear",            label: tr("data.creationYear"),      essential: true },
              { key: "checkEstimatedValue",  label: tr("data.estimatedValue"),    essential: true },
              { key: "checkDocuments",       label: tr("data.invoiceCertificate"), essential: false },
              { key: "checkInventoryNumber", label: tr("data.inventoryRef"),      essential: false },
              { key: "checkDescription",     label: tr("data.description"),       essential: false },
              { key: "checkAcquisitionPrice",label: tr("data.acquisitionPrice"),  essential: false },
            ].map(({ key, label, essential }) => (
              <label key={key} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${options[key] ? "border-gray-900 bg-gray-50" : "border-gray-200 bg-white hover:border-gray-400"}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-black"
                  checked={!!options[key]}
                  onChange={() => setOptions({ ...options, [key]: !options[key] })}
                />
                <span className="text-sm text-gray-800">{label}</span>
                {essential && <span className="text-xs text-amber-600 font-medium ml-auto">{tr("completeness.essential")}</span>}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">{tr("completeness.note")}</p>
        </div>
      )}

      {/* OPTIONS */}
      <div className={`bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8 ${reportType === "completude" ? "opacity-40 pointer-events-none" : ""}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Gauche */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {tr("options.title")}
            </h2>
            <div className="space-y-3">
              {[
                {
                  label: tr("options.includeDate"),
                  key: "includeDate",
                },
                {
                  label:
                    tr("options.includeThumbnails"),
                  key: "includeThumbnails",
                },
                {
                  label: tr("options.valueTotals"),
                  key: "includeValueTotal",
                },
                {
                  label: tr("options.purchaseTotal"),
                  key: "includePurchaseValue",
                },
              ].map(({ label, key }) => (
                <div key={key} className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id={key}
                      type="checkbox"
                      className="h-4 w-4 text-neutral-600 border-gray-300 rounded focus:ring-neutral-500"
                      checked={options[key]}
                      onChange={() =>
                        setOptions({
                          ...options,
                          [key]: !options[key],
                        })
                      }
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label
                      htmlFor={key}
                      className="text-gray-700"
                    >
                      {label}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Droite */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {tr("data.title")}
            </h2>
            <div className="space-y-3">
              {[
                {
                  label: tr("data.artist"),
                  key: "includeArtist",
                },
                {
                  label: tr("data.location"),
                  key: "includeLocation",
                },
                {
                  label: tr("data.priceEstimated"),
                  key: "includePrice",
                },
                {
                  label: tr("data.valueSource"),
                  key: "includeValueSource",
                },
                {
                  label: tr("data.dimensions"),
                  key: "includeSize",
                },
                {
                  label: tr("data.mediumShort"),
                  key: "includeMedium",
                },
              ].map(({ label, key }) => (
                <div key={key} className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id={key}
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      checked={options[key]}
                      onChange={() =>
                        setOptions({
                          ...options,
                          [key]: !options[key],
                        })
                      }
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label
                      htmlFor={key}
                      className="text-gray-700"
                    >
                      {label}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* WHITE-LABEL OPTIONS - Pour Art Advisor Premium et Family Office */}
      {enableWhiteLabel && (
        <div className="bg-neutral-50 p-6 rounded-lg shadow-sm border border-neutral-200 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                {tr("whiteLabel.title")}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {tr("whiteLabel.description")}
              </p>
            </div>
            <div className="flex items-center">
              <input
                id="enableWhiteLabel"
                type="checkbox"
                className="h-5 w-5 text-neutral-600 border-gray-300 rounded focus:ring-neutral-500"
                checked={enableWhiteLabel}
                onChange={() => setEnableWhiteLabel(!enableWhiteLabel)}
              />
              <label
                htmlFor="enableWhiteLabel"
                className="ml-3 text-sm font-medium text-gray-700"
              >
                {tr("whiteLabel.enable")}
              </label>
            </div>
          </div>

          {enableWhiteLabel && (
            <div className="bg-white p-4 rounded border border-gray-200 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tr("whiteLabel.company")}
                </label>
                <p className="text-sm text-gray-600 mb-2">
                  {tr("whiteLabel.current")} : {userProfile?.company_display_name || userProfile?.company_name || userProfile?.full_name || tr("common.notProvided")}
                </p>
                <Link
                  to="/profile"
                  className="text-sm text-neutral-600 hover:text-neutral-800 underline"
                >
                  {tr("whiteLabel.editProfile")} →
                </Link>
              </div>

              {userProfile?.company_logo_url ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tr("whiteLabel.logo")}
                  </label>
                  <div className="flex items-center space-x-4">
                    <img
                      src={userProfile.company_logo_url}
                      alt="Logo"
                      className="h-12 w-auto border border-gray-200 rounded"
                    />
                    <Link
                      to="/profile"
                      className="text-sm text-neutral-600 hover:text-neutral-800 underline"
                    >
                      {tr("whiteLabel.changeLogo")} →
                    </Link>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tr("whiteLabel.logo")}
                  </label>
                  <p className="text-sm text-gray-600 mb-2">
                    {tr("whiteLabel.noLogo")}
                  </p>
                  <Link
                    to="/profile"
                    className="text-sm text-neutral-600 hover:text-neutral-800 underline"
                  >
                    {tr("whiteLabel.addLogo")} →
                  </Link>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  💡 {tr("whiteLabel.note")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MISE EN PAGE */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {tr("layout.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("layout.groupBy")}
            </label>
            <select
              value={options.groupBy}
              onChange={(e) =>
                setOptions({
                  ...options,
                  groupBy: e.target.value,
                })
              }
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            >
              <option value="none">{tr("layout.none")}</option>
              <option value="artist">{tr("layout.artist")}</option>
              <option value="location">{tr("layout.location")}</option>
              <option value="medium">{tr("layout.medium")}</option>
              <option value="year">{tr("layout.year")}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("layout.sortBy")}
            </label>
            <select
              value={options.sortBy}
              onChange={(e) =>
                setOptions({
                  ...options,
                  sortBy: e.target.value,
                })
              }
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
            >
              <option value="name">{tr("layout.titleSort")}</option>
              <option value="artist">{tr("layout.artist")}</option>
              <option value="value">{tr("layout.value")}</option>
              <option value="acquisitionDate">
                {tr("layout.acquisitionDate")}
              </option>
              <option value="creationYear">
                {tr("data.creationYear")}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleGenerate}
          disabled={isLoading}
          className={`inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-white ${
            isLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#1A1A1A] hover:bg-[#333]"
          }`}
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {tr("actions.generating")}
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                ></path>
              </svg>
              {tr("actions.generatePdf")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CollectionsReports;
